import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Trash2, 
  Download,
  Eye,
  Calendar,
  User,
  Phone,
  MapPin,
  Search
} from 'lucide-react';
import { Input } from '../ui/input';
import axios from 'axios';
import { generateDevizPDF } from '../../utils/pdfGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DevizeManager = ({ token }) => {
  const [devize, setDevize] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeviz, setSelectedDeviz] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDevize();
  }, []);

  const fetchDevize = async () => {
    try {
      const response = await axios.get(`${API}/admin/devize`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevize(response.data);
    } catch (error) {
      console.error('Error fetching devize:', error);
      toast.error('Eroare la încărcarea devizelor');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDeviz = (deviz) => {
    setSelectedDeviz(deviz);
    setShowDetailDialog(true);
  };

  const handleDownloadDeviz = (deviz) => {
    const quoteData = {
      roofTypeName: deviz.roof_type,
      subType: deviz.sub_type,
      items: deviz.items,
      totalPrice: deviz.total_cu_tva
    };
    
    const doc = generateDevizPDF(quoteData, {
      clientName: deviz.client_name,
      clientPhone: deviz.client_phone,
      projectName: deviz.project_name,
      devizNumber: deviz.deviz_number
    });
    
    // Generate filename
    const createdAt = new Date(deviz.created_at);
    const dateStr = `${createdAt.getDate().toString().padStart(2, '0')}.${(createdAt.getMonth() + 1).toString().padStart(2, '0')}.${createdAt.getFullYear()}`;
    const clientNameClean = deviz.client_name.replace(/[^a-zA-Z0-9]/g, '_');
    const filename = `${deviz.deviz_number}-${dateStr}-${clientNameClean}.pdf`;
    
    doc.save(filename);
    toast.success('PDF descărcat!');
  };

  const handleDeleteDeviz = async (devizId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest deviz?')) return;
    
    try {
      await axios.delete(`${API}/admin/devize/${devizId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deviz șters');
      fetchDevize();
    } catch (error) {
      console.error('Error deleting deviz:', error);
      toast.error('Eroare la ștergerea devizului');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const filteredDevize = devize.filter(d => 
    d.deviz_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.project_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="p-4 text-center">Se încarcă...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Devize Generate</h2>
          <p className="text-slate-600">{devize.length} devize salvate</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Caută deviz..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
        </div>
      </div>

      {/* Devize Table */}
      {filteredDevize.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nr. Deviz</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Proiect</TableHead>
                  <TableHead>Total cu TVA</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDevize.map((deviz) => (
                  <TableRow key={deviz.id} data-testid={`deviz-row-${deviz.id}`}>
                    <TableCell className="font-mono font-bold text-cyan-600">
                      {deviz.deviz_number}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{deviz.client_name}</p>
                        {deviz.client_phone && (
                          <p className="text-xs text-slate-500">{deviz.client_phone}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {deviz.project_name || deviz.roof_type}
                      </span>
                    </TableCell>
                    <TableCell className="font-bold text-green-600">
                      {deviz.total_cu_tva.toFixed(2)} RON
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {formatDate(deviz.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDeviz(deviz)}
                          data-testid={`view-deviz-${deviz.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDeviz(deviz)}
                          className="text-cyan-600"
                          data-testid={`download-deviz-${deviz.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteDeviz(deviz.id)}
                          data-testid={`delete-deviz-${deviz.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600">
              {searchTerm ? 'Nu s-au găsit devize pentru căutarea ta.' : 'Nu există devize salvate încă.'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Devizele sunt salvate automat când utilizatorii descarcă PDF din calculator.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-600" />
              Detalii Deviz {selectedDeviz?.deviz_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedDeviz && (
            <div className="space-y-6">
              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <User className="w-4 h-4" />
                    Client
                  </div>
                  <p className="font-medium">{selectedDeviz.client_name}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Phone className="w-4 h-4" />
                    Telefon
                  </div>
                  <p className="font-medium">{selectedDeviz.client_phone || '-'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <MapPin className="w-4 h-4" />
                    Proiect
                  </div>
                  <p className="font-medium">{selectedDeviz.project_name || '-'}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                    <Calendar className="w-4 h-4" />
                    Data
                  </div>
                  <p className="font-medium">{formatDate(selectedDeviz.created_at)}</p>
                </div>
              </div>
              
              {/* Items Table */}
              <div>
                <h4 className="font-medium mb-3">Articole ({selectedDeviz.items.length})</h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Operațiune</TableHead>
                        <TableHead>Material</TableHead>
                        <TableHead className="text-right">Cantitate</TableHead>
                        <TableHead className="text-right">Preț</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedDeviz.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">{item.phaseName}</TableCell>
                          <TableCell className="text-sm">{item.materialName}</TableCell>
                          <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                          <TableCell className="text-right">
                            {(item.unitPrice + item.unitLabor).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {item.total.toFixed(2)} RON
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              
              {/* Totals */}
              <div className="bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Manoperă:</span>
                    <span className="font-medium">{selectedDeviz.total_manopera.toFixed(2)} RON</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Materiale:</span>
                    <span className="font-medium">{selectedDeviz.total_material.toFixed(2)} RON</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total fără TVA:</span>
                    <span className="font-medium">{selectedDeviz.total_fara_tva.toFixed(2)} RON</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">TVA (21%):</span>
                    <span className="font-medium">{selectedDeviz.tva.toFixed(2)} RON</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-cyan-200 flex justify-between items-center">
                  <span className="font-bold text-lg text-cyan-800">TOTAL CU TVA:</span>
                  <span className="font-bold text-2xl text-cyan-700">
                    {selectedDeviz.total_cu_tva.toFixed(2)} RON
                  </span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Închide
            </Button>
            <Button 
              onClick={() => selectedDeviz && handleDownloadDeviz(selectedDeviz)}
              className="btn-primary"
            >
              <Download className="w-4 h-4 mr-2" />
              Descarcă PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DevizeManager;
